import React, { useState, useEffect, useCallback } from 'react';
import { adminAccounts } from '../../utils/api';
import { useAdminAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const ROLES = [
  { value: 'superadmin', label: 'Super Admin', desc: 'Full access including managing other admins' },
  { value: 'admin',      label: 'Admin',       desc: 'Full operational access (orders, products, settings)' },
  { value: 'manager',    label: 'Manager',     desc: 'Operational access — reserved for narrower role gating later' },
];

const emptyAdmin = { email: '', password: '', full_name: '', role: 'admin', is_active: 1 };

const AdminAccounts = () => {
  const { admin: currentAdmin } = useAdminAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyAdmin);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    adminAccounts.getAll()
      .then(setAdmins)
      .catch(err => toast.error(err.message || 'Failed to load admins'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setForm(emptyAdmin);
    setEditing(null);
    setShowForm(true);
  };

  const openEdit = (a) => {
    setForm({
      email: a.email,
      password: '',           // empty = don't change
      full_name: a.full_name,
      role: a.role,
      is_active: a.is_active ? 1 : 0,
    });
    setEditing(a.id);
    setShowForm(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? (checked ? 1 : 0) : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      // On edit, send password only if user filled it in
      if (editing && !payload.password) delete payload.password;

      if (editing) {
        await adminAccounts.update(editing, payload);
        toast.success('Admin updated');
      } else {
        await adminAccounts.create(payload);
        toast.success('Admin created');
      }
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (a) => {
    if (a.id === currentAdmin?.id) {
      toast.error('You cannot delete your own account');
      return;
    }
    if (!confirm(`Delete admin ${a.email}? This cannot be undone.`)) return;
    try {
      await adminAccounts.delete(a.id);
      toast.success('Admin deleted');
      load();
    } catch (err) {
      toast.error(err.message || 'Delete failed');
    }
  };

  const roleColor = (r) =>
    r === 'superadmin' ? 'rgba(212,148,58,0.2)' :
    r === 'admin'      ? 'rgba(99,148,105,0.2)' :
                          'rgba(120,120,120,0.2)';
  const roleText = (r) =>
    r === 'superadmin' ? '#92400e' :
    r === 'admin'      ? '#166534' :
                          '#525252';

  return (
    <div className="admin-page">
      <div className="admin-page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div>
          <h1>Admin Accounts</h1>
          <p style={{ color:'var(--text-muted)', fontSize:'0.88rem', marginTop:4 }}>
            Manage who can log into the dashboard and what role they have.
          </p>
        </div>
        <button className="btn btn-primary" onClick={openNew}>+ New Admin</button>
      </div>

      <div className="admin-card">
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" style={{ textAlign:'center', padding:40 }}><div className="spinner" style={{ margin:'0 auto' }} /></td></tr>
              ) : admins.length === 0 ? (
                <tr><td colSpan="6" style={{ textAlign:'center', padding:30, color:'var(--text-muted)' }}>No admins yet</td></tr>
              ) : admins.map(a => (
                <tr key={a.id}>
                  <td>
                    <div style={{ fontWeight:600 }}>
                      {a.full_name}
                      {a.id === currentAdmin?.id && (
                        <span style={{ marginLeft:8, fontSize:'0.7rem', color:'var(--gold)', fontWeight:700 }}>(YOU)</span>
                      )}
                    </div>
                  </td>
                  <td style={{ fontSize:'0.88rem' }}>{a.email}</td>
                  <td>
                    <span className="badge" style={{ background: roleColor(a.role), color: roleText(a.role) }}>
                      {ROLES.find(r => r.value === a.role)?.label || a.role}
                    </span>
                  </td>
                  <td>
                    {a.is_active
                      ? <span className="badge badge-olive">Active</span>
                      : <span className="badge badge-red">Disabled</span>}
                  </td>
                  <td style={{ fontSize:'0.82rem', color:'var(--text-muted)' }}>
                    {a.last_login ? new Date(a.last_login).toLocaleString() : '—'}
                  </td>
                  <td>
                    <div style={{ display:'flex', gap:6 }}>
                      <button className="action-btn" onClick={() => openEdit(a)}>Edit</button>
                      {a.id !== currentAdmin?.id && (
                        <button className="action-btn action-btn--danger" onClick={() => handleDelete(a)}>Delete</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="modal-overlay">
          <div className="modal modal--lg">
            <div className="modal-header">
              <h2>{editing ? 'Edit Admin' : 'New Admin'}</h2>
              <button className="modal-close" onClick={() => setShowForm(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit} className="modal-body">
              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">Full Name *</label>
                  <input name="full_name" value={form.full_name} onChange={handleChange}
                         className="form-control" required placeholder="Eslam Atef" />
                </div>
                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input name="email" type="email" value={form.email} onChange={handleChange}
                         className="form-control" required placeholder="admin@qayakw.com" />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  Password {editing
                    ? <span style={{ color:'var(--text-muted)', fontWeight:400 }}>— leave blank to keep current</span>
                    : ' *'}
                </label>
                <input name="password" type="password" value={form.password} onChange={handleChange}
                       className="form-control"
                       placeholder={editing ? '••••••••' : 'Min 8 characters'}
                       required={!editing}
                       minLength={editing ? 0 : 8} />
              </div>

              <div className="form-group">
                <label className="form-label">Role *</label>
                <select name="role" value={form.role} onChange={handleChange} className="form-control">
                  {ROLES.map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
                <p style={{ fontSize:'0.78rem', color:'var(--text-muted)', marginTop:6 }}>
                  {ROLES.find(r => r.value === form.role)?.desc}
                </p>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input type="checkbox" name="is_active" checked={!!form.is_active} onChange={handleChange} />
                  Account active (uncheck to disable login without deleting)
                </label>
              </div>

              {editing && editing === currentAdmin?.id && (
                <p style={{
                  fontSize:'0.85rem', background:'rgba(212,148,58,0.1)',
                  border:'1px solid var(--gold)', borderRadius:8, padding:12, marginTop:8
                }}>
                  ⚠ This is your own account. To prevent lockout, you can't change your role away from
                  Super Admin or deactivate yourself here.
                </p>
              )}

              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : (editing ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAccounts;
